// Strong plate-to-car associations, decided deterministically from the mark
// itself and from owner-confirmed knowledge readings. Used to pick the
// PlateViewer hero (F80 FER shows a Ferrari, not the car it happens to be on).
//
// Only STRONG evidence counts. A model number on its own is never enough
// (a plate containing 911 is not automatically a Porsche), and neither is an
// unconfirmed reading. A plate is associated with a car when:
//   1. a make abbreviation is a whole letter group AND a model of that make is
//      in the rest of the mark            F80 FER, FER 296S, 911 POR, R8 AUD
//   2. a full make name, digits read as letters, spans whole groups with only
//      a number, a year letter or a model left over
//                                         LAM 80 (LAMBO), BMW 1, AUD 1
//   3. an owner-confirmed knowledge reading of kind car or brand names the make
//                                         930 TBO, 992 AB, 849 T
// Anything else returns null and the hero falls back to vehicle history.

import { PlateKnowledgeReading } from '../services/plate-knowledge.service';

export interface PlateCarAssociation {
  /** Normalised make, as viewer-car-meta uses it ('ferrari', 'landrover'). */
  make: string;
  /** Model text matched against catalogue families ('F80', '911'), '' for a make-only link. */
  model: string;
  /** Approximate launch year of the model, for picking a car of the same era. */
  year: number | null;
  /** Hypercar: prefer the catalogue's hypercars when the exact model is missing. */
  hyper?: boolean;
  /** What established the link. */
  source: 'mark' | 'knowledge';
}

interface ModelEntry {
  token: string;
  year: number;
  hyper?: boolean;
  /** Model text for catalogue matching when it differs from the token (930 -> 911 Turbo). */
  as?: string;
}

// Models per make. Tokens are matched against the mark as written (no digit
// substitution) and must not run into further digits (F80 matches F80, not
// F800). Years are launch years, approximate.
const MODELS: { [make: string]: ModelEntry[] } = {
  ferrari: [
    { token: 'F80', year: 2024, hyper: true },
    { token: 'LAFERRARI', year: 2013, hyper: true },
    { token: 'ENZO', year: 2002, hyper: true },
    { token: 'F50', year: 1995, hyper: true },
    { token: 'F40', year: 1987, hyper: true },
    { token: 'SF90', year: 2019 },
    { token: '849', year: 2025 },
    { token: '296', year: 2021 },
    { token: '812', year: 2017 },
    { token: 'F12', year: 2012 },
    { token: '488', year: 2015 },
    { token: '458', year: 2009 },
    { token: 'F8', year: 2019 },
    { token: '430', year: 2004 },
    { token: '360', year: 1999 },
    { token: '355', year: 1994 },
    { token: '550', year: 1996 },
    { token: '575', year: 2002 },
    { token: '599', year: 2006 },
    { token: '612', year: 2004 },
    { token: 'ROMA', year: 2020 },
    { token: '250', year: 1957 },
    { token: '308', year: 1975 },
    { token: '328', year: 1985 },
    { token: '348', year: 1989 },
  ],
  porsche: [
    { token: '918', year: 2013, hyper: true },
    { token: '959', year: 1986, hyper: true },
    { token: '911', year: 2019 },
    { token: '992', year: 2019, as: '911' },
    { token: '991', year: 2011, as: '911' },
    { token: '997', year: 2004, as: '911' },
    { token: '996', year: 1998, as: '911' },
    { token: '993', year: 1994, as: '911' },
    { token: '964', year: 1989, as: '911' },
    { token: '930', year: 1975, as: 'TURBO' },
    { token: '718', year: 2016 },
    { token: 'GT3', year: 2021 },
    { token: 'GT2', year: 2017 },
    { token: '356', year: 1948 },
    { token: '928', year: 1977 },
    { token: '944', year: 1982 },
  ],
  lamborghini: [
    { token: 'REVUELTO', year: 2023, hyper: true },
    { token: 'SVJ', year: 2018 },
    { token: 'AVENTADOR', year: 2011 },
    { token: 'HURACAN', year: 2014 },
    { token: 'URUS', year: 2018 },
    { token: 'COUNTACH', year: 1974 },
    { token: 'DIABLO', year: 1990 },
    { token: 'MIURA', year: 1966 },
  ],
  mclaren: [
    { token: 'P1', year: 2013, hyper: true },
    { token: 'F1', year: 1992, hyper: true },
    { token: 'SENNA', year: 2018, hyper: true },
    { token: '765', year: 2020 },
    { token: '750', year: 2023 },
    { token: '720', year: 2017 },
    { token: '675', year: 2015 },
    { token: '650', year: 2014 },
    { token: '600', year: 2018 },
    { token: '570', year: 2015 },
    { token: '12C', year: 2011 },
  ],
  audi: [
    { token: 'R8', year: 2015 },
    { token: 'RS3', year: 2021 },
    { token: 'RS4', year: 2018 },
    { token: 'RS5', year: 2017 },
    { token: 'RS6', year: 2019 },
    { token: 'RS7', year: 2019 },
    { token: 'TT', year: 2014 },
  ],
  bmw: [
    { token: 'M1', year: 1978 },
    { token: 'M2', year: 2016 },
    { token: 'M3', year: 2021 },
    { token: 'M4', year: 2021 },
    { token: 'M5', year: 2018 },
    { token: 'M8', year: 2019 },
    { token: 'I8', year: 2014 },
    { token: 'Z4', year: 2019 },
  ],
  mercedes: [
    { token: 'SLR', year: 2003 },
    { token: 'SLS', year: 2010 },
    { token: 'G63', year: 2018 },
    { token: 'GT', year: 2015 },
  ],
};

// Abbreviations: only ever strong together with a model of the same make.
const ABBREVIATIONS: { [abbr: string]: string } = {
  FER: 'ferrari',
  FRR: 'ferrari',
  POR: 'porsche',
  LAM: 'lamborghini',
  MCL: 'mclaren',
  AUD: 'audi',
};

// Full names (and the universal nicknames), strong on their own.
const MAKE_WORDS: { [word: string]: string } = {
  FERRARI: 'ferrari',
  PORSCHE: 'porsche',
  LAMBO: 'lamborghini',
  LAMBORGHINI: 'lamborghini',
  MCLAREN: 'mclaren',
  AUDI: 'audi',
  BMW: 'bmw',
  AMG: 'mercedes',
  MERCEDES: 'mercedes',
  BENTLEY: 'bentley',
  ROLLSROYCE: 'rollsroyce',
  ASTON: 'astonmartin',
  BUGATTI: 'bugatti',
  // No Tesla: the catalogue has none a UK plate fits, so a Tesla link would
  // only land on an unrelated car.
};

// Digits as the letters they read as on a plate.
const AS_LETTER: { [d: string]: string } = {
  '0': 'O', '1': 'I', '3': 'E', '4': 'A', '5': 'S', '6': 'G', '7': 'T', '8': 'B',
};

function findModel(make: string, text: string): ModelEntry | null {
  for (const m of MODELS[make] || []) {
    let i = text.indexOf(m.token);
    while (i >= 0) {
      const runsIn = /^\d/.test(m.token) && /\d/.test(text.charAt(i - 1));
      const runsOut = /\d$/.test(m.token) && /\d/.test(text.charAt(i + m.token.length));
      if (!runsIn && !runsOut) return m;
      i = text.indexOf(m.token, i + 1);
    }
  }
  return null;
}

function association(make: string, model: ModelEntry | null, source: 'mark' | 'knowledge'): PlateCarAssociation {
  return {
    make,
    model: model ? model.as || model.token : '',
    year: model ? model.year : null,
    hyper: model?.hyper || undefined,
    source,
  };
}

/** Rules 1 and 2: the mark alone. */
export function associationFromMark(registration: string | null | undefined): PlateCarAssociation | null {
  const reg = (registration || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const groups = reg.match(/[A-Z]+|[0-9]+/g) || [];
  if (!groups.length) return null;

  // 1. Abbreviation as a whole letter group + a model of that make elsewhere.
  for (let g = 0; g < groups.length; g++) {
    const make = ABBREVIATIONS[groups[g]];
    if (!make) continue;
    const model = findModel(make, groups.filter((_, i) => i !== g).join(''));
    if (model) return association(make, model, 'mark');
  }

  // 2. Full make name read with digit substitution, spanning whole groups
  //    (CLAUD1A is not AUDI: the word starts inside a group).
  const read = reg.replace(/[0-9]/g, (d) => AS_LETTER[d] || d);
  const bounds = new Set<number>([0]);
  let pos = 0;
  groups.forEach((gr) => bounds.add((pos += gr.length)));
  for (const word of Object.keys(MAKE_WORDS)) {
    let i = read.indexOf(word);
    while (i >= 0) {
      const end = i + word.length;
      if (bounds.has(i) && bounds.has(end)) {
        const make = MAKE_WORDS[word];
        const rest = reg.slice(0, i) + reg.slice(end);
        const model = findModel(make, rest);
        // Left over: nothing, a plain number, a year letter, or a model.
        if (model || !rest || /^\d{1,3}$/.test(rest) || /^[A-Z]$/.test(rest)) {
          return association(make, model, 'mark');
        }
      }
      i = read.indexOf(word, i + 1);
    }
  }
  return null;
}

/** Rule 3: an owner-confirmed car or brand reading names the make. */
export function associationFromReadings(
  readings: PlateKnowledgeReading[] | null | undefined
): PlateCarAssociation | null {
  for (const r of readings || []) {
    if (!r || !r.confirmed || (r.kind !== 'car' && r.kind !== 'brand')) continue;
    for (const text of [r.car || '', r.reads_as || '']) {
      const compact = text.toUpperCase().replace(/[^A-Z0-9]/g, '');
      const word = Object.keys(MAKE_WORDS).find((w) => compact.includes(w));
      if (!word) continue;
      const make = MAKE_WORDS[word];
      return association(make, findModel(make, compact.replace(word, '')), 'knowledge');
    }
  }
  return null;
}

/** The plate's strong car association, or null. Owner-confirmed knowledge first. */
export function plateCarAssociation(
  registration: string | null | undefined,
  readings?: PlateKnowledgeReading[] | null
): PlateCarAssociation | null {
  return associationFromReadings(readings) || associationFromMark(registration);
}
